import { ChipProps, GridProps } from '@mui/material';

import { SxStyle } from '../../utils/reactTypes';
import { AxleChip, ChipsInRows } from '../displayFields/chips';
import { CappedChipRows, useCappedList } from '../displayFields/expandableChips';
import { DisciplineChipFragment } from './DisciplineChip.fragments';

export { DisciplineChipFragment };

interface IDisciplineChipProps {
    discipline: IDisciplineChip;
    sx?: SxStyle;
    // The chip shows the discipline's abbreviation; the tooltip expands it to the
    // full name (e.g. "Physical Therapy (PT)"), which is useful in dense tables.
    // Opt out where surrounding chips have no tooltip and the mix looks inconsistent
    // (e.g. the clinician details header). Defaults to on.
    showTooltip?: boolean;
}

export interface IDisciplineChip {
    id: string;
    name: string | null;
    displayName: string;
    code: string;
}

export const DisciplineChip = ({
    discipline,
    showTooltip = true,
    ...rest
}: IDisciplineChipProps) => {
    return (
        <AxleChip
            label={discipline.displayName}
            tooltip={
                showTooltip
                    ? discipline.name
                        ? `${discipline.name} (${discipline.code})`
                        : discipline.code
                    : undefined
            }
            {...rest}
        />
    );
};

export const DisciplineChips = ({
    disciplines,
    chipProps,
    previewCount = Infinity,
    showTooltip = true,
    ...props
}: {
    disciplines: IDisciplineChipProps['discipline'][];
    chipProps?: ChipProps;
    // Cap the list and reveal the rest via a "Show all N" toggle. Defaults to no
    // cap so existing callers are unaffected.
    previewCount?: number;
    // Passed through to each chip; see DisciplineChip. Defaults to on.
    showTooltip?: boolean;
} & GridProps) => {
    const { shown, total, canToggle, showAll, toggle } = useCappedList(disciplines, previewCount);
    return (
        <CappedChipRows canToggle={canToggle} showAll={showAll} total={total} onToggle={toggle}>
            <ChipsInRows {...props}>
                {shown.map((discipline) => (
                    <DisciplineChip
                        key={discipline.id}
                        discipline={discipline}
                        showTooltip={showTooltip}
                        {...chipProps}
                    />
                ))}
            </ChipsInRows>
        </CappedChipRows>
    );
};
